import { useEffect, useMemo, useRef, useState } from "react";
import { getImageUrl } from "../../../utils/media";
import CarouselArrow from "../../CarouselArrow/CarouselArrow";
import "./style.css";

const DEFAULT_TABS = [
  { key: "atendidos", label: "Atendidos" },
  { key: "familiares", label: "Familiares" },
  { key: "colaboradores", label: "Colaboradores" },
  { key: "parceiros", label: "Parceiros" },
];

const CATEGORY_LABELS = Object.fromEntries(
  DEFAULT_TABS.map((tab) => [tab.key, tab.label]),
);

function getTabs(categories) {
  if (!Array.isArray(categories) || categories.length === 0) {
    return DEFAULT_TABS;
  }

  return categories.map((category) => ({
    key: category,
    label: CATEGORY_LABELS[category] || category,
  }));
}

export default function Depoimentos({
  institutionalPhrase,
  section,
  categories,
  testimonials,
  isLoading,
  hasLoadError,
}) {
  const tabs = useMemo(() => getTabs(categories), [categories]);
  const [activeTab, setActiveTab] = useState("atendidos");
  const [currentIndex, setCurrentIndex] = useState(0);
  const indoBordaRef = useRef(null);

  useEffect(() => {
    if (!tabs.some((tab) => tab.key === activeTab)) {
      setActiveTab(tabs[0]?.key || "");
    }
  }, [activeTab, tabs]);

  const lista = useMemo(() => {
    if (!Array.isArray(testimonials)) {
      return [];
    }

    return testimonials.filter((testimonial) => testimonial.category === activeTab);
  }, [activeTab, testimonials]);

  useEffect(() => {
    if (indoBordaRef.current === "fim" && lista.length > 0) {
      setCurrentIndex(lista.length - 1);
    } else {
      setCurrentIndex(0);
    }

    indoBordaRef.current = null;
  }, [activeTab, lista.length]);

  const changeTab = (key) => {
    indoBordaRef.current = null;
    setActiveTab(key);
  };

  const irParaAbaAdjacente = (direcao) => {
    const indiceAtual = tabs.findIndex((tab) => tab.key === activeTab);
    const total = tabs.length;

    if (total === 0) return;

    const proximoIndice = (indiceAtual + direcao + total) % total;
    indoBordaRef.current = direcao === 1 ? "inicio" : "fim";
    setActiveTab(tabs[proximoIndice].key);
  };

  const prev = () => {
    if (lista.length === 0 || currentIndex === 0) {
      irParaAbaAdjacente(-1);
      return;
    }

    setCurrentIndex((index) => index - 1);
  };

  const next = () => {
    if (lista.length === 0 || currentIndex === lista.length - 1) {
      irParaAbaAdjacente(1);
      return;
    }

    setCurrentIndex((index) => index + 1);
  };

  const atual = lista[currentIndex];
  const photoUrl = getImageUrl(atual?.photoUrl);

  return (
    <section className="depoimentos">
      <div className="depoimentos__inner">
        <h2 className="depoimentos__titulo-principal">
          {institutionalPhrase || "Transformando vidas e impactando gerações"}
        </h2>

        <div className="depoimentos__cabecalho">
          <h3 className="depoimentos__titulo-secao">
            {section?.title || "Depoimentos"}
          </h3>
          <nav className="depoimentos__tabs">
            {tabs.map((tab) => (
              <button
                key={tab.key}
                type="button"
                className={`depoimentos__tab ${activeTab === tab.key ? "depoimentos__tab--ativo" : ""}`}
                onClick={() => changeTab(tab.key)}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        <div className="depoimentos__carrossel">
          <CarouselArrow
            direction="prev"
            className="depoimentos__seta"
            onClick={prev}
            ariaLabel="Depoimento anterior"
          />

          {isLoading ? (
            <p className="depoimentos__vazio">Carregando...</p>
          ) : hasLoadError ? (
            <p className="depoimentos__vazio">
              Não foi possível carregar os depoimentos.
            </p>
          ) : lista.length === 0 ? (
            <p className="depoimentos__vazio">
              Ainda não há depoimentos nessa categoria.
            </p>
          ) : (
            <div className="depoimentos__card">
              <div className="depoimentos__foto">
                {photoUrl ? (
                  <img src={photoUrl} alt={atual.name} />
                ) : (
                  <div className="depoimentos__foto-placeholder" />
                )}
              </div>
              <div className="depoimentos__texto">
                <p>{atual.text}</p>
                <strong>{atual.name}</strong>
                {(atual.subtitle || atual.nucleo || atual.local) && (
                  <span>{atual.subtitle || atual.nucleo || atual.local}</span>
                )}
              </div>
            </div>
          )}

          <CarouselArrow
            direction="next"
            className="depoimentos__seta"
            onClick={next}
            ariaLabel="Próximo depoimento"
          />
        </div>
      </div>
    </section>
  );
}
